import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { authService } from '@/services'
import { useAuthStore } from '@/stores/authStore'
import { HttpError } from '@/types/errors'
import { getAuthCode, isDingTalkEnvironment, showDingTalkToast } from '@/utils/dingtalk'

type StepState = 'idle' | 'checkEnv' | 'checkCode' | 'checkLogin' | 'completed' | 'failed'
type StepStatus = 'pending' | 'loading' | 'success' | 'failure'

interface DingtalkState {
  loading: boolean
  step: StepState
  error: string | null
  
  // 各步骤状态
  envStatus: StepStatus
  codeStatus: StepStatus
  loginStatus: StepStatus
}

interface DingtalkActions {
  doLoading: () => void
  doLoaded: () => void
  setError: (error: string | null) => void
  resetStates: () => void
  doLogin: () => Promise<boolean>
  
  // 各步骤动作
  checkEnvironment: () => Promise<boolean>
  getAuthorizationCode: () => Promise<string | null>
  verifyUserIdentity: (authCode: string) => Promise<boolean>
}

type DingtalkStore = DingtalkState & DingtalkActions

const initialState: DingtalkState = {
  loading: false,
  step: 'idle',
  error: null,
  envStatus: 'pending',
  codeStatus: 'pending',
  loginStatus: 'pending'
}

const useDingtalkStore = create<DingtalkStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      doLoading: () => set({ loading: true }),
      doLoaded: () => set({ loading: false }),
      setError: (error: string | null) => set({ error }),
      resetStates: () => set({
        ...initialState,
        step: 'idle'
      }),

      // 检测钉钉环境
      checkEnvironment: async (): Promise<boolean> => {
        set({ step: 'checkEnv', envStatus: 'loading' })
        
        try {
          const isInDingTalk = isDingTalkEnvironment()

          if (isInDingTalk) {

            // 钉钉初始化，暂时没有调用敏感接口，暂不需要签名
            // await initDingTalkJSAPI()

            set({ envStatus: 'success' })
            return true
          } else {
            set({ 
              envStatus: 'failure', 
              step: 'failed',
              error: '当前不在钉钉环境中' 
            })
            return false
          }
        } catch {
          set({
            envStatus: 'failure',
            step: 'failed',
            error: '检测钉钉环境失败'
          })
          return false
        }
      },

      // 获取授权码
      getAuthorizationCode: async (): Promise<string | null> => {
        set({ step: 'checkCode', codeStatus: 'loading' })
        
        try {
          const authCode = await getAuthCode()
          set({ codeStatus: 'success' })
          return authCode
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '获取授权码失败'
          set({ 
            codeStatus: 'failure', 
            step: 'failed',
            error: errorMessage 
          })
          return null
        }
      },

      // 验证用户身份
      verifyUserIdentity: async (authCode: string): Promise<boolean> => {
        set({ step: 'checkLogin', loginStatus: 'loading' })
        
        try {
          const response = await authService.enterpriseLogin(
            authCode,
            import.meta.env.VITE_DINGTALK_CORP_ID
          )

          if (response.success) {
            const { token, refreshToken, user } = response.data
            useAuthStore.getState().setAuth(token, refreshToken, user)
            
            set({ 
              loginStatus: 'success', 
              step: 'completed' 
            })
            
            await showDingTalkToast('success', '登录成功！')
            return true
          } else {
            set({ 
              loginStatus: 'failure', 
              step: 'failed',
              error: response.message || '登录失败' 
            })
            return false
          }
        } catch (error: unknown | HttpError) {
          const errorMessage = error instanceof Error 
            ? error.message 
            : (error as HttpError)?.response?.data?.message || '登录失败'
            
          set({ 
            loginStatus: 'failure', 
            step: 'failed',
            error: errorMessage 
          })
          
          await showDingTalkToast('error', `登录失败：${errorMessage}`)
          return false
        }
      },

      // 完整的企业登录流程
      doLogin: async (): Promise<boolean> => {
        const store = get()
        
        try {
          set({ loading: true, error: null })
          
          // 步骤1: 检测钉钉环境
          const isEnvOk = await store.checkEnvironment()
          if (!isEnvOk) {
            return false
          }
          
          // 步骤2: 获取授权码
          const authCode = await store.getAuthorizationCode()
          if (!authCode) {
            return false
          }
          
          // 步骤3: 验证用户身份
          const isLoginOk = await store.verifyUserIdentity(authCode)
          return isLoginOk
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '登录过程发生未知错误'
          set({ 
            step: 'failed',
            error: errorMessage 
          })
          return false
        } finally {
          set({ loading: false })
        }
      }
    }),
    {
      name: 'dingtalk-storage'
    }
  )
)

export default useDingtalkStore
